import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import RecordRTC from "recordrtc";
import { uploadVideoInChunks } from "../../common/utils/api";
import { axiosPost, axiosPostMultipart } from "../../common/utils/helper";
import { AxiosResponse } from "axios";

interface RecordingContextType {
  isRecording: boolean;
  errors: string | null;
  startRecording: () => Promise<string | null>;
  stopRecording: () => Promise<File | null>;
  clearError: () => void;
  onScreenRecordingEnd: (callback: () => void) => void;
}

const RecordingContext = createContext<RecordingContextType | undefined>(
  undefined,
);

export const RecordingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const recorderRef = useRef<RecordRTC | null>(null);
  const cameraRecorderRef = useRef<RecordRTC | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [errors, setError] = useState<string | null>(null);

  const screenEndCallbacks = useRef<(() => void)[]>([]);
  const failedChunksRef = useRef<
    { blob: Blob; chunkIndex: number; attempt: number; recordingType: string }[]
  >([]);

  const chunkIndexRef = useRef(0);
  const cameraChunkIndexRef = useRef(0);

  const micStream = useRef<MediaStream | null>(null);
  const screenStream = useRef<MediaStream | null>(null);
  const cameraStream = useRef<MediaStream | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);

  //Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      revokeAllPermissions();
      if (recorderRef.current) {
        recorderRef.current.destroy();
        recorderRef.current = null;
      }
      if (cameraRecorderRef.current) {
        cameraRecorderRef.current.destroy();
        cameraRecorderRef.current = null;
      }
    };
  }, []);

  /** ---------------- Permissions ---------------- */
  const revokeAllPermissions = useCallback(() => {
    [micStream.current, screenStream.current, cameraStream.current].forEach(
      (stream) => {
        stream?.getTracks().forEach((track) => track.stop());
      },
    );

    micStream.current = null;
    screenStream.current = null;
    cameraStream.current = null;

    if (audioContextRef.current) {
      // Check if context is not already closed
      if (audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }

    console.log("✅ All permissions reset.");
  }, []);

  const getPreferredMic = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(
        (device) => device.kind === "audioinput",
      );
      const defaultMic = audioInputs.find(
        (device) => device.deviceId === "default",
      );
      return defaultMic?.label.toLowerCase().includes("webcam") || false;
    } catch (err) {
      console.error("Error checking mic:", err);
      return false;
    }
  };

  /** ---------------- Chunk Upload ---------------- */
  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const sendChunkToBackend = async (
    blob: Blob,
    chunkIndex: number,
    attempt: number,
    recordingType: string,
  ) => {
    try {
      const formData = new FormData();
      formData.append("videoChunk", blob, `${recordingType}_chunk_${chunkIndex}.webm`);
      formData.append("filename", `recordedChunks`);
      formData.append("chunkIndex", String(chunkIndex));
      formData.append("mimeType", "video/webm");
      formData.append("recordingType", recordingType);

      //  Only retry failed chunks if not currently uploading
      if (!isRecording) {
        await retryFailedChunks();
      }

      (await axiosPostMultipart(uploadVideoInChunks, formData)) as AxiosResponse;
    } catch (err) {
      console.error(
        `❌ Error uploading chunk ${chunkIndex} (attempt ${attempt}):`,
        err,
      );
      if (attempt <= 5) {
        const delay = Math.min(Math.pow(2, attempt) * 1000, 10000);
        setTimeout(
          () =>
            sendChunkToBackend(blob, chunkIndex, attempt + 1, recordingType),
          delay,
        );
      } else {
        failedChunksRef.current.push({
          blob,
          chunkIndex,
          attempt: 1,
          recordingType,
        });
      }
    }
  };

  const retryFailedChunks = async () => {
    if (failedChunksRef.current.length === 0) return;
    console.log(`Retrying ${failedChunksRef.current.length} failed chunks...`);

    const stillFailed: typeof failedChunksRef.current = [];
    for (const {
      blob,
      chunkIndex,
      attempt,
      recordingType,
    } of failedChunksRef.current) {
      try {
        await sendChunkToBackend(blob, chunkIndex, attempt, recordingType);
      } catch {
        stillFailed.push({
          blob,
          chunkIndex,
          attempt: attempt + 1,
          recordingType,
        });
      }
    }
    failedChunksRef.current = stillFailed;
  };

  /** ---------------- Recording ---------------- */
  const startRecording = useCallback(async (): Promise<string | null> => {
    const isMobile =
      /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(
        navigator.userAgent,
      ) || navigator.maxTouchPoints > 1;

    // Add early return if already recording
    if (isRecording) {
      return "Recording is already in progress.";
    }

    //  Reset chunk indices when starting new recording
    chunkIndexRef.current = 0;
    cameraChunkIndexRef.current = 0;

    try {
      micStream.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (await getPreferredMic()) {
        return `1. Right-click the 🔊 sound icon in the taskbar and open "Sound settings".
        2. Under Input, select your preferred microphone (avoid the webcam mic).
        3. Test the mic using the "Test your microphone" bar.
        4. Refresh the browser to apply changes.`;
      }
    } catch {
      return "Microphone permission denied.";
    }

    try {
      cameraStream.current = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 320 },
          height: { ideal: 240 },
          frameRate: { ideal: 10 }
        },
      });
    } catch {
      //  Clean up mic stream if camera fails
      revokeAllPermissions();
      return "Camera permission denied.";
    }

    // if (!isMobile) {
      try {
        screenStream.current = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { max: 1280 },
            height: { max: 720 },
            frameRate: { max: 5 }
          },
          audio: true,
        });
        const videoTrack = screenStream.current.getVideoTracks()[0];

        //  Store the event handler so it can be properly cleaned up
        videoTrack.onended = () => {
          screenEndCallbacks.current.forEach((cb) => cb());
          //  Auto-stop recording when screen sharing ends
          if (isRecording) {
            stopRecording();
          }
        };

        const settings = videoTrack?.getSettings() as any;
        if (settings?.displaySurface !== "monitor") {
          revokeAllPermissions();
          return "Full screen permission denied.";
        }
        if (screenStream.current.getAudioTracks().length === 0) {
          revokeAllPermissions();
          return "System audio is not shared.";
        }
      } catch {
        revokeAllPermissions();
        return "Screen recording permission denied.";
      }
    // }

    try {
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext; //  Store reference for cleanup

      //  Only create screen audio source if screen stream exists
      const screenAudio = screenStream.current
        ? audioContext.createMediaStreamSource(screenStream.current)
        : null;
      const micAudio = audioContext.createMediaStreamSource(micStream.current);

      const gainNode = audioContext.createGain();
      gainNode.gain.value = 2.5; // Increase microphone volume

      micAudio.connect(gainNode);

      const destination = audioContext.createMediaStreamDestination();
      screenAudio?.connect(destination);
      gainNode.connect(destination);

      const combinedStream = isMobile
        ? new MediaStream(destination.stream.getAudioTracks())
        : new MediaStream([
          ...(screenStream.current?.getVideoTracks() || []),
          ...destination.stream.getAudioTracks(),
        ]);

      recorderRef.current = new RecordRTC(combinedStream, {
        type: "video",
        mimeType: "video/webm",
        timeSlice: 60000,
        videoBitsPerSecond: 200000,
        ondataavailable: (blob) => {
          chunkIndexRef.current++;
          sendChunkToBackend(blob, chunkIndexRef.current, 1, "screenStream");
          try {
            const internal = (recorderRef.current as any)?.getInternalRecorder();
            if (internal && typeof internal.getArrayOfBlobs === "function") {
              const blobsArray = internal.getArrayOfBlobs();
              if (Array.isArray(blobsArray)) {
                blobsArray.length = 0;
              }
            }
          } catch (err) {
            console.error("Error clearing screen recorder buffer:", err);
          }
        },
      });

      cameraRecorderRef.current = new RecordRTC(cameraStream.current!, {
        type: "video",
        mimeType: "video/webm",
        timeSlice: 60000,
        videoBitsPerSecond: 100000,
        ondataavailable: (blob) => {
          cameraChunkIndexRef.current++;
          sendChunkToBackend(
            blob,
            cameraChunkIndexRef.current,
            1,
            "cameraStream",
          );
          try {
            const internal = (cameraRecorderRef.current as any)?.getInternalRecorder();
            if (internal && typeof internal.getArrayOfBlobs === "function") {
              const blobsArray = internal.getArrayOfBlobs();
              if (Array.isArray(blobsArray)) {
                blobsArray.length = 0;
              }
            }
          } catch (err) {
            console.error("Error clearing camera recorder buffer:", err);
          }
        },
      });

      recorderRef.current.startRecording();
      cameraRecorderRef.current.startRecording();
      setIsRecording(true);

      return null;
    } catch (err) {
      console.error("Error starting recording:", err);
      revokeAllPermissions(); //  Clean up on error
      return "An error occurred while starting the recording.";
    }
  }, [isRecording, revokeAllPermissions]); //  Add missing dependency

  const stopRecording = useCallback(
    (): Promise<File | null> =>
      new Promise((resolve) => {
        //  Add safety check
        if (!isRecording) {
          resolve(null);
          return;
        }

        const stopOne = (
          rec: RecordRTC | null,
          fileName: string,
        ): File | null => {
          if (!rec) return null;
          try {
            rec.stopRecording();
            const blob = rec.getBlob();
            //  Destroy recorder to free resources
            rec.destroy();
            return blob
              ? new File([blob], fileName, { type: "video/mp4" })
              : null;
          } catch (err) {
            console.error(`Error stopping ${fileName}:`, err);
            return null;
          }
        };

        const file = stopOne(recorderRef.current, "recorded_screen.mp4");
        stopOne(cameraRecorderRef.current, "recorded_camera.mp4");

        recorderRef.current = null;
        cameraRecorderRef.current = null;

        setIsRecording(false);
        revokeAllPermissions();

        //  Add final retry of failed chunks after stopping
        retryFailedChunks().finally(() => {
          resolve(file);
        });
      }),
    [isRecording, revokeAllPermissions], //  Add missing dependency
  );

  const clearError = useCallback(() => setError(null), []);

  const onScreenRecordingEnd = useCallback((callback: () => void) => {
    screenEndCallbacks.current.push(callback);
  }, []);

  return (
    <RecordingContext.Provider
      value={{
        isRecording,
        errors,
        startRecording,
        stopRecording,
        clearError,
        onScreenRecordingEnd,
      }}
    >
      {children}
    </RecordingContext.Provider>
  );
};

/** Custom hook */
export const useRecording = () => {
  const context = useContext(RecordingContext);
  if (!context)
    throw new Error("useRecording must be used within a RecordingProvider");
  return context;
};
